import React, { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext(undefined);

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem("cravve-cart");
      if (saved) return JSON.parse(saved);
    } catch (e) {
      // ignore corrupt cart
    }
    return { restaurantId: null, restaurantName: null, items: [] };
  });

  useEffect(() => {
    localStorage.setItem("cravve-cart", JSON.stringify(cart));
  }, [cart]);

  const addItem = (item, restaurantId, restaurantName) => {
    setCart((prev) => {
      if (prev.restaurantId && prev.restaurantId !== restaurantId) {
        const ok = window.confirm(
          "You have items from another restaurant in your cart. Clear them and start fresh?"
        );
        if (!ok) return prev;
        return { restaurantId, restaurantName, items: [item] };
      }
      const existing = prev.items.find((i) => i.menuItemId === item.menuItemId);
      if (existing) {
        return {
          ...prev,
          restaurantId,
          restaurantName,
          items: prev.items.map((i) =>
            i.menuItemId === item.menuItemId ? { ...i, quantity: i.quantity + item.quantity } : i
          ),
        };
      }
      return { restaurantId, restaurantName, items: [...prev.items, item] };
    });
  };

  const removeItem = (menuItemId) => {
    setCart((prev) => {
      const items = prev.items.filter((i) => i.menuItemId !== menuItemId);
      return {
        restaurantId: items.length === 0 ? null : prev.restaurantId,
        restaurantName: items.length === 0 ? null : prev.restaurantName,
        items,
      };
    });
  };

  const updateQuantity = (menuItemId, quantity) => {
    if (quantity <= 0) return removeItem(menuItemId);
    setCart((prev) => ({
      ...prev,
      items: prev.items.map((i) => (i.menuItemId === menuItemId ? { ...i, quantity } : i)),
    }));
  };

  const clearCart = () =>
    setCart({ restaurantId: null, restaurantName: null, items: [] });

  const totalItems = cart.items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = cart.items.reduce((s, i) => s + i.price * i.quantity, 0);

  return (
    <CartContext.Provider
      value={{ cart, addItem, removeItem, updateQuantity, clearCart, totalItems, subtotal }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
